(() => {
  if (window.nativeProductCardReady) return;
  window.nativeProductCardReady = true;

  const cardSelector = '[data-native-product-card]';
  let variantResizeObserver;
  let textMeasureContext;

  function selectedVariant(card) {
    return card.querySelector('[data-native-variant]')?.selectedOptions[0] || null;
  }

  function syncVariantCaption(card, variant) {
    const caption = card.querySelector('[data-native-selected-variant]');
    const select = card.querySelector('[data-native-variant]');
    if (!caption || !select || !variant) return;
    const title = variant.dataset.title || variant.textContent.trim();
    caption.textContent = title;
    if (title === 'Default Title') {
      caption.hidden = true;
      return;
    }

    const style = window.getComputedStyle(select);
    const availableWidth = select.clientWidth - ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth']
      .reduce((total, property) => total + (parseFloat(style[property]) || 0), 0);
    if (availableWidth <= 0) {
      caption.hidden = false;
      return;
    }
    if (!textMeasureContext) textMeasureContext = document.createElement('canvas').getContext('2d');
    if (!textMeasureContext) {
      caption.hidden = false;
      return;
    }
    textMeasureContext.font = style.font || `${style.fontSize} ${style.fontFamily}`;
    caption.hidden = textMeasureContext.measureText(variant.textContent.trim()).width <= availableWidth;
  }

  function savingFor(card, variant, plan) {
    if (!plan || card.dataset.requiresPlan === 'true') return '';
    const oneTimeCents = Number(variant.dataset.priceCents);
    const planCents = Number(plan.dataset.priceCents);
    if (!Number.isSafeInteger(oneTimeCents) || !Number.isSafeInteger(planCents) ||
      oneTimeCents <= 0 || planCents < 0 || planCents >= oneTimeCents || !plan.dataset.savingAmount) return '';

    const exactPercent = (oneTimeCents - planCents) * 100 / oneTimeCents;
    const roundedPercent = Math.round(exactPercent);
    const percent = roundedPercent > 0 && Math.abs(exactPercent - roundedPercent) < 0.05
      ? ` (${roundedPercent}%)` : '';
    return `${card.dataset.saveLabel} ${plan.dataset.savingAmount}${percent}`;
  }

  function syncNotifyDialog(dialog, variant) {
    if (!dialog || !variant) return;
    const id = variant.id || variant.value;
    const title = variant.title || variant.dataset?.title || variant.textContent?.trim() || '';
    const available = variant.available === true || variant.available === 'true' || variant.dataset?.available === 'true';
    dialog.dataset.variantId = String(id);
    dialog.dataset.variantAvailable = String(available);
    const name = dialog.querySelector('[data-native-notify-variant]');
    if (name) {
      name.hidden = title === 'Default Title';
      name.textContent = name.hidden ? '' : `: ${title}`;
    }
    const trigger = Array.from(document.querySelectorAll('[data-native-notify]'))
      .find(node => node.getAttribute('aria-controls') === dialog.id);
    if (trigger) trigger.hidden = available;
    if (available && dialog.open) dialog.close();
  }

  function syncCard(card, announce = false) {
    const variant = selectedVariant(card);
    const form = card.querySelector('[data-native-product-form]');
    if (!variant || !form) return;

    syncVariantCaption(card, variant);

    const nutrition = card.querySelector('[data-native-nutrition]');
    if (nutrition) {
      const facts = Array.from(card.querySelectorAll('template[data-native-nutrition-for]'))
        .find(node => node.dataset.nativeNutritionFor === variant.value);
      nutrition.replaceChildren();
      if (facts) nutrition.append(facts.content.cloneNode(true));
      nutrition.hidden = !facts;
    }

    const planSelect = form.querySelector('[data-native-plan]');
    const subscription = form.querySelector('[data-native-subscription]');
    const oneTime = form.querySelector('[data-native-one-time]');
    const frequency = form.querySelector('[data-native-frequency]');
    const template = Array.from(form.querySelectorAll('template[data-native-plans-for]'))
      .find(node => node.dataset.nativePlansFor === variant.value);
    const previousPlan = planSelect && planSelect.value;
    const clearanceBadge = card.querySelector('[data-native-clearance-badge]');
    const clearanceIds = card.dataset.clearanceVariants || clearanceBadge?.dataset.clearanceVariants || '';
    const isClearance = clearanceIds.split(',').includes(variant.value);

    if (planSelect) {
      planSelect.replaceChildren();
      if (template && !isClearance) planSelect.append(template.content.cloneNode(true));
      if (previousPlan && Array.from(planSelect.options).some(option => option.value === previousPlan)) {
        planSelect.value = previousPlan;
      }
    }

    const available = variant.dataset.available === 'true';
    if (clearanceBadge) clearanceBadge.hidden = !isClearance;
    const hasPlans = Boolean(planSelect && planSelect.options.length);
    const purchase = form.querySelector('[data-native-purchase]');
    if (purchase) purchase.hidden = !available || !hasPlans;
    if (subscription) subscription.disabled = !available || !hasPlans;
    if (subscription && !hasPlans) subscription.checked = false;
    if (oneTime && (!available || !hasPlans)) oneTime.checked = true;
    if (subscription && !oneTime && available && hasPlans) subscription.checked = true;
    const subscribed = Boolean(available && subscription && subscription.checked && hasPlans);
    if (frequency) frequency.hidden = !subscribed;
    if (planSelect) planSelect.disabled = !subscribed;

    const plan = subscribed ? planSelect.selectedOptions[0] : null;
    const offerPlan = hasPlans ? planSelect.selectedOptions[0] : null;
    const saving = available ? savingFor(card, variant, offerPlan) : '';
    const offer = form.querySelector('[data-native-subscribe-offer]');
    const offerPrice = form.querySelector('[data-native-subscribe-price]');
    const savings = form.querySelector('[data-native-savings]');
    if (offerPrice) {
      offerPrice.textContent = offerPlan ? offerPlan.dataset.price : '';
      offerPrice.hidden = subscribed;
    }
    if (savings) {
      savings.textContent = saving;
      savings.hidden = !saving;
    }
    if (offer) offer.hidden = !offerPlan || !available || (subscribed && !saving);

    const price = plan || variant;
    card.querySelector('[data-native-price]').textContent = price.dataset.price;
    const compare = card.querySelector('[data-native-compare]');
    compare.textContent = subscribed ? saving ? variant.dataset.price : '' : variant.dataset.compare || '';
    compare.hidden = !compare.textContent;
    const priceNote = card.querySelector('[data-native-price-note]');
    if (priceNote) priceNote.hidden = !subscribed;
    if (announce) {
      const announcement = form.querySelector('[data-native-price-announcement]');
      if (announcement) announcement.textContent = `${subscribed ? card.dataset.subscriptionPriceLabel : card.dataset.oneTimePriceLabel} ${price.dataset.price}${subscribed && saving ? `. ${saving}` : ''}`;
    }

    const image = card.querySelector('[data-native-image]');
    if (image && variant.dataset.image) {
      if (image.getAttribute('src') !== variant.dataset.image) image.src = variant.dataset.image;
      image.alt = variant.dataset.imageAlt || '';
    }

    const eligible = !card.dataset.requiresPlan || card.dataset.requiresPlan === 'false' || subscribed;
    const submit = form.querySelector('[data-native-submit]');
    submit.disabled = !available || !eligible || form.dataset.submitting === 'true';
    submit.hidden = !available;
    const submitLabel = submit.querySelector('[data-native-submit-label]') || submit;
    submitLabel.textContent = !available ? card.dataset.soldOutLabel : eligible ? card.dataset.addLabel : card.dataset.unavailableLabel;
    const notifyDialog = card.querySelector('[data-native-notify-dialog]');
    syncNotifyDialog(notifyDialog, variant);
    card.querySelector('[data-native-stock]').textContent = available ? card.dataset.inStockLabel : card.dataset.soldOutLabel;
  }

  function openNotify(trigger) {
    const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!dialog || dialog.dataset.variantAvailable === 'true' || typeof dialog.showModal !== 'function') return;
    const status = dialog.querySelector('[data-native-notify-status]');
    if (status) status.textContent = '';
    const submit = dialog.querySelector('[data-native-notify-submit]');
    if (submit) submit.disabled = false;
    dialog.showModal();
    dialog.querySelector('input[type="email"]')?.focus();
  }

  async function submitNotify(form) {
    const dialog = form.closest('[data-native-notify-dialog]');
    if (!dialog || form.dataset.submitting === 'true') return;
    const status = form.querySelector('[data-native-notify-status]');
    if (!/^\d+$/.test(dialog.dataset.variantId || '') || dialog.dataset.variantAvailable === 'true') {
      status.textContent = dialog.dataset.notifyErrorLabel;
      return;
    }
    const email = form.elements.namedItem('email');
    if (!email || !email.reportValidity()) return;
    const button = form.querySelector('[data-native-notify-submit]');
    form.dataset.submitting = 'true';
    button.disabled = true;
    status.textContent = dialog.dataset.notifySendingLabel;
    try {
      const response = await fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=RHdejJ', {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'strict-origin-when-cross-origin',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-02-15', 'x-klaviyo-onsite': '1' },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: email.value.trim() } } },
              channels: ['EMAIL']
            },
            relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${dialog.dataset.variantId}` } } }
          }
        })
      });
      if (!response.ok) throw new Error(dialog.dataset.notifyErrorLabel);
      status.textContent = dialog.dataset.notifySuccessLabel;
    } catch (_) {
      status.textContent = dialog.dataset.notifyErrorLabel;
      button.disabled = false;
    } finally {
      delete form.dataset.submitting;
    }
  }

  async function addToCart(form) {
    if (form.dataset.submitting === 'true') return;
    const card = form.closest(cardSelector);
    const variant = selectedVariant(card);
    const submit = form.querySelector('[data-native-submit]');
    const status = form.querySelector('[data-native-status]');
    const planSelect = form.querySelector('[data-native-plan]');
    const subscription = form.querySelector('[data-native-subscription]');
    const subscribed = Boolean(subscription && subscription.checked);

    if (variant.dataset.available !== 'true' || (subscribed && (!planSelect || !planSelect.value))) {
      status.textContent = card.dataset.unavailableLabel;
      return;
    }

    const payload = { id: Number(variant.value), quantity: 1 };
    if (subscribed) payload.selling_plan = Number(planSelect.value);
    form.dataset.submitting = 'true';
    submit.disabled = true;
    status.textContent = card.dataset.addingLabel;

    try {
      const route = window.routes && window.routes.cartAddUrl || form.action;
      const response = await fetch(route + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || card.dataset.errorLabel);
      status.textContent = card.dataset.addedLabel;
      card.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: { variant: result, quantity: 1, button: submit }
      }));
    } catch (error) {
      status.textContent = error.message || card.dataset.errorLabel;
    } finally {
      delete form.dataset.submitting;
      syncCard(card);
    }
  }

  function initializeDialogs(root) {
    const dialogs = root.matches && root.matches('[data-native-notify-dialog]') ? [root] : root.querySelectorAll('[data-native-notify-dialog]');
    dialogs.forEach(dialog => {
      if (dialog.dataset.nativeNotifyInitialized === 'true') return;
      dialog.dataset.nativeNotifyInitialized = 'true';
      dialog.addEventListener('close', () => {
        const trigger = Array.from(document.querySelectorAll('[data-native-notify]'))
          .find(node => node.getAttribute('aria-controls') === dialog.id);
        if (trigger && !trigger.hidden) trigger.focus();
      });
      dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const focusable = Array.from(dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled])'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });
  }

  function initialize(root) {
    const cards = root.matches && root.matches(cardSelector) ? [root] : root.querySelectorAll(cardSelector);
    cards.forEach(card => {
      if (card.dataset.nativeInitialized === 'true') return;
      card.dataset.nativeInitialized = 'true';
      syncCard(card);
      const select = card.querySelector('[data-native-variant]');
      if (select && variantResizeObserver) variantResizeObserver.observe(select);
    });
    initializeDialogs(root);
  }

  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-native-notify]');
    if (trigger) openNotify(trigger);
    const close = event.target.closest('[data-native-notify-close]');
    if (close) close.closest('[data-native-notify-dialog]')?.close();
  });

  document.addEventListener('change', event => {
    const notifySelect = event.target.closest('[data-native-notify-variant-select]');
    if (notifySelect) {
      const dialog = notifySelect.closest('[data-native-notify-dialog]');
      const selected = notifySelect.selectedOptions[0];
      if (dialog && selected) {
        syncNotifyDialog(dialog, selected);
        dialog.querySelector('[data-native-notify-status]').textContent = '';
      }
      return;
    }
    const card = event.target.closest(cardSelector);
    if (card && event.target.matches('[data-native-variant], [data-native-one-time], [data-native-subscription], [data-native-plan]')) {
      card.querySelector('[data-native-status]').textContent = '';
      syncCard(card, true);
    }
  });

  document.addEventListener('submit', event => {
    const notifyForm = event.target.closest('[data-native-notify-form]');
    if (notifyForm) {
      event.preventDefault();
      submitNotify(notifyForm);
      return;
    }
    const form = event.target.closest('[data-native-product-form]');
    if (!form) return;
    event.preventDefault();
    addToCart(form);
  });

  const start = () => {
    if ('ResizeObserver' in window) {
      variantResizeObserver = new ResizeObserver(entries => entries.forEach(entry => {
        const card = entry.target.closest(cardSelector);
        if (card) syncVariantCaption(card, selectedVariant(card));
      }));
    }
    initialize(document);
    new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(node => {
        if (node.nodeType === 1) initialize(node);
      }));
    }).observe(document.body, { childList: true, subtree: true });
  };

  window.addEventListener('pageshow', () => {
    document.querySelectorAll(cardSelector).forEach(syncCard);
  });

  window.addEventListener('resize', () => {
    document.querySelectorAll(cardSelector).forEach(card => syncVariantCaption(card, selectedVariant(card)));
  });

  window.nativeBackInStock = { sync: syncNotifyDialog };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
